/**
 * Path-based explorer URLs for dashboards + templates.
 *
 *   /dashboards
 *   /dashboards/<dashboardPath>
 *   /dashboards/f/<folderPath>
 *   /dashboards/templates
 *   /dashboards/templates/<templatePath>
 *   /dashboards/templates/f/<folderPath>
 *
 * Paths are UNIQUE per organization in Postgres (`dashboards_org_path_key`,
 * `templates_org_path_key`, and the matching folder constraints), so the
 * path alone identifies a row. Reserved first segments under `/dashboards`:
 * `templates`, `autogenerate`, `f`.
 */

export const DASHBOARDS_HOME = '/dashboards'
export const TEMPLATES_HOME = '/dashboards/templates'

/**
 * Shared by the Dashboards and Templates explorer rails so collapsing one
 * tab keeps the other collapsed after a route swap.
 */
export const DASHBOARDS_EXPLORER_OPEN_KEY = 'ontology.dashboards.explorerSidebar.open'

/** Encode a slash-separated resource path for use in a URL pathname. */
export function encodeResourcePath(path: string): string {
  if (path === '') return ''
  return path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')
}

/** Decode a splat captured from `/dashboards/…` or `/dashboards/f/…`. */
export function decodeResourcePath(splat: string | undefined): string {
  if (splat === undefined || splat === '') return ''
  return splat
    .split('/')
    .filter((segment) => segment.length > 0)
    .map((segment) => {
      try {
        return decodeURIComponent(segment)
      } catch {
        return segment
      }
    })
    .join('/')
}

export function dashboardHref(path: string): string {
  const encoded = encodeResourcePath(path)
  if (encoded === '') return DASHBOARDS_HOME
  return `${DASHBOARDS_HOME}/${encoded}`
}

export function dashboardFolderHref(path: string): string {
  if (path === '') return DASHBOARDS_HOME
  return `${DASHBOARDS_HOME}/f/${encodeResourcePath(path)}`
}

export function templateHref(path: string): string {
  const encoded = encodeResourcePath(path)
  if (encoded === '') return TEMPLATES_HOME
  return `${TEMPLATES_HOME}/${encoded}`
}

export function templateFolderHref(path: string): string {
  if (path === '') return TEMPLATES_HOME
  return `${TEMPLATES_HOME}/f/${encodeResourcePath(path)}`
}

/**
 * Proxy-owned folder holding one agent-linked dashboard per custom agent
 * (`Agent Insights/<agent name>`). The proxy refuses renaming, moving, or
 * deleting the folder and creating unlinked dashboards in it, so the
 * explorer treats the folder itself as locked. Dashboards inside it are
 * ordinary rows.
 */
export const AGENT_INSIGHTS_FOLDER_PATH = 'Agent Insights'

export function isAgentInsightsFolderPath(path: string | null | undefined): boolean {
  return path === AGENT_INSIGHTS_FOLDER_PATH
}
